import React, { FC, useCallback, useEffect, useState } from "react";

import { getMediaPlayer } from "@keepeek/api-client";
import { getExtension } from "@keepeek/commons";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from "@mui/material";
import { FilePondErrorDescription, FilePondFile } from "filepond";
import { FilePond } from "react-filepond";
import { useTranslation } from "react-i18next";

import { VideoResolutionQuality } from "../../../../../../containers/widgets/KVideoWidget/utils";
import { getAxiosClientInstance } from "../../../../../../lib/axios/axios-utils";
import { ApiStoreType } from "../../../../../../providers/admin/config/atoms";
import { addNewValueInApiStoreMap } from "../../../../../../providers/admin/config/utils";
import logger from "../../../../../logger-utils";
import {
  KEEPICKER_VIDEO_CLIENT_ID,
  LoadingStatus,
  META_MODEL_TYPE_VIDEO,
  META_MODEL_TYPE_INTERNAL_NAME,
} from "../../utils";

import "filepond/dist/filepond.min.css";

type DialogVideoProps = {
  open: boolean;
  onClose: () => void;
  resourceUploadState: LoadingStatus;
  setResourceUploadState: (status: LoadingStatus) => void;
  files: any[];
  setFiles: (files: any[]) => void;
  widgetId: string | undefined;
  handleAddFile: (file: FilePondFile) => Promise<{
    id: string;
    url: string;
    previewUrl?: string;
    originalFileHeight?: string;
    originalFileWidth?: string;
  }>;
  handleOnChange: (value: {
    id: string;
    url: string;
    previewUrl?: string;
    videoPreviewUrl?: string;
    originalFileHeight?: string;
    originalFileWidth?: string;
    permalinks?: Array<{
      title: string;
      url: string;
    }>;
  }) => void;
  key?: string;
  setApiStore: React.Dispatch<React.SetStateAction<ApiStoreType>>;
  setDataChange: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentResourceUrl: (url: string) => void;
  setCurrentResourceVideoPreviewUrl: (url: string) => void;
  isKeepickerInRealm: boolean;
};

const DialogVideo: FC<DialogVideoProps> = function ({
  open,
  onClose,
  resourceUploadState,
  setResourceUploadState,
  files,
  setFiles,
  widgetId,
  handleAddFile,
  handleOnChange,
  key,
  setApiStore,
  setDataChange,
  setCurrentResourceUrl,
  setCurrentResourceVideoPreviewUrl,
  isKeepickerInRealm,
}) {
  const { t } = useTranslation();
  const [openKpkKeepicker, setOpenKpkKeepicker] = useState(false);
  const [keepickerElement, setKeepickerElement] = useState<HTMLElement | null>(null);

  const handleKeepickerRef = useCallback((node: HTMLElement | null) => {
    const videoValuePermanentFilters = [META_MODEL_TYPE_VIDEO];
    const videoInternalFiedNamePermanentFilters = META_MODEL_TYPE_INTERNAL_NAME;
    setKeepickerElement(node);
    if (node) {
      const config = {
        permanentFilters: [
          {
            values: videoValuePermanentFilters,
            type: "LISTFIELD",
            internalFieldName: videoInternalFiedNamePermanentFilters,
            fieldType: "MEDIAFIELD",
            modifier: "EQUALS_ONE",
          },
        ],
      };

      const value = JSON.stringify(config);
      node.setAttribute("custom-global-configuration", value);
    }
  }, []);

  useEffect(() => {
    if (!open || !keepickerElement || !isKeepickerInRealm) {
      return;
    }

    const handleInsert = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const element = customEvent.detail.element;
      const elementId = element.id;
      const permalinks = element.permalinks;
      const previewUrl = element.previewUrl;
      const originalFileName = element.originalFileName;
      const originalFileHeight = element.fields?.find((field) => field.id === "height")?.value;
      const originalFileWidth = element.fields?.find((field) => field.id === "width")?.value;

      // Récupérer l'URL du permalink avec title "preview"
      const previewPermalink = permalinks?.find(
        (p) => p.title === "preview" && p.url.includes(".mp4"),
      );
      const previewPermalinkUrl = previewPermalink?.url;

      // Vérifier le format du fichier
      const ALLOWED_EXTENSIONS = ["mp4", "mov", "mpg", "wmv", "avi", "mpeg", "flv", "vob", "m4v"];
      const isValidFileFormat = (filename: string): boolean => {
        const extension = getExtension(filename);
        return extension !== undefined && ALLOWED_EXTENSIONS.includes(extension);
      };

      // Utiliser l'URL "auto" comme URL principale si disponible, sinon la première permalink et en .mp4, webp, mov, mpg, wmv, avi, mpeg, flv, vob, m4v
      const primaryUrl =
        permalinks?.find((p) => p.url?.includes(".mp4"))?.url ||
        permalinks?.find((p) => p.url?.includes(".mov"))?.url ||
        permalinks?.find((p) => p.url?.includes(".mpg"))?.url ||
        permalinks?.find((p) => p.url?.includes(".wmv"))?.url ||
        permalinks?.find((p) => p.url?.includes(".avi"))?.url ||
        permalinks?.find((p) => p.url?.includes(".mpeg"))?.url ||
        permalinks?.find((p) => p.url?.includes(".flv"))?.url ||
        permalinks?.find((p) => p.url?.includes(".vob"))?.url ||
        permalinks?.find((p) => p.url?.includes(".m4v"))?.url ||
        "";
      const permalinksArray = permalinks?.filter(
        (p) =>
          p.url?.includes(".mp4") &&
          [
            VideoResolutionQuality._1080,
            VideoResolutionQuality._720,
            VideoResolutionQuality._480,
            VideoResolutionQuality.AUTO,
          ].includes(p.title),
      ); // on garde uniquement les permalinks en .mp4 et les title = 1080p, 720p, 480p, auto

      const fileNameToCheck = originalFileName || primaryUrl || "";
      if (!isValidFileFormat(fileNameToCheck)) {
        setResourceUploadState(LoadingStatus.ERROR);
        return;
      }

      try {
        // Récupérer toutes les informations du média via getMediaPlayer
        const mediaId = typeof elementId === "number" ? elementId : parseInt(elementId, 10);
        if (!isNaN(mediaId)) {
          const playerResponse = await getMediaPlayer(getAxiosClientInstance(), {
            mediaId: mediaId,
          });
          const playerConfig = playerResponse.data;

          // Utiliser les sources de la configuration pour enrichir les permalinks si disponibles
          const enrichedPermalinks =
            playerConfig.sources && playerConfig.sources.length > 0
              ? playerConfig.sources.map((source) => ({
                  title: source.label || `${source.res}p`,
                  url: source.src,
                }))
              : permalinksArray;

          // Utiliser posterSrc de la configuration si disponible, sinon utiliser previewUrl
          const finalPreviewUrl = playerConfig.posterSrc || previewUrl;

          // Les dimensions (height/width) restent celles de l'élément original
          // car les sources contiennent la résolution (res) et non les dimensions exactes
          const finalHeight = originalFileHeight;
          const finalWidth = originalFileWidth;

          // Utiliser l'URL de la source par défaut si disponible
          const defaultSource = playerConfig.sources?.find((s) => s.default);
          const finalPrimaryUrl = defaultSource?.src || primaryUrl;

          //set permalinks and previewUrl in the form data
          setCurrentResourceUrl(finalPrimaryUrl);
          setCurrentResourceVideoPreviewUrl(previewPermalinkUrl || "");
          handleOnChange({
            id: elementId.toString(),
            url: finalPrimaryUrl,
            previewUrl: finalPreviewUrl,
            videoPreviewUrl: previewPermalinkUrl,
            originalFileHeight: finalHeight,
            originalFileWidth: finalWidth,
            permalinks: enrichedPermalinks,
          });
        } else {
          // Fallback si l'ID n'est pas un nombre valide
          setCurrentResourceUrl(primaryUrl);
          setCurrentResourceVideoPreviewUrl(previewPermalinkUrl || "");
          handleOnChange({
            id: elementId.toString(),
            url: primaryUrl,
            previewUrl: previewUrl,
            videoPreviewUrl: previewPermalinkUrl,
            originalFileHeight: originalFileHeight,
            originalFileWidth: originalFileWidth,
            permalinks: permalinksArray,
          });
        }
      } catch (error) {
        logger.error("Error while fetching media player configuration", error);
        // En cas d'erreur, utiliser les données de base
        setCurrentResourceUrl(primaryUrl);
        setCurrentResourceVideoPreviewUrl(previewPermalinkUrl || "");
        handleOnChange({
          id: elementId.toString(),
          url: primaryUrl,
          previewUrl: previewUrl,
          videoPreviewUrl: previewPermalinkUrl,
          originalFileHeight: originalFileHeight,
          originalFileWidth: originalFileWidth,
          permalinks: permalinksArray,
        });
      }

      setResourceUploadState(LoadingStatus.LOADED);
      // Fermer la dialog après sélection
      onClose();
      setOpenKpkKeepicker(false);
    };

    keepickerElement.addEventListener("kpk-insert", handleInsert);

    return () => {
      keepickerElement.removeEventListener("kpk-insert", handleInsert);
    };
  }, [
    open,
    keepickerElement,
    isKeepickerInRealm,
    handleOnChange,
    setCurrentResourceUrl,
    setCurrentResourceVideoPreviewUrl,
    setResourceUploadState,
    onClose,
  ]);

  const handleClose = () => {
    setOpenKpkKeepicker(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ textAlign: "center" }}>
        <Typography sx={{ fontWeight: "bold" }} variant="body1">
          {t("admin:resource.upload.title")}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <>
          <Box
            sx={{
              ...(resourceUploadState === LoadingStatus.LOADING && { display: "none" }),
              paddingBottom: 0.5,
              cursor: "pointer",
            }}
          >
            <FilePond
              id={widgetId || ""}
              files={resourceUploadState === LoadingStatus.NONE ? undefined : files}
              onupdatefiles={(files) => setFiles(files)}
              onaddfilestart={() => setResourceUploadState(LoadingStatus.LOADING)}
              allowMultiple={false}
              name="files"
              maxFileSize={"50MB"}
              labelMaxFileSizeExceeded={
                t("admin:resource.upload.error.maxFileSizeExceeded") as string
              }
              labelMaxFileSize={t("admin:resource.upload.error.maxFileSize") as string}
              labelIdle={t("admin:resource.upload.label") as string}
              onaddfile={async (error: FilePondErrorDescription | null, file: FilePondFile) => {
                if (error === null) {
                  const resource = await handleAddFile(file);
                  if (resource) {
                    if (!key) {
                      handleOnChange({
                        id: resource.id,
                        url: resource.url,
                        previewUrl: resource.previewUrl,
                        originalFileHeight: resource.originalFileHeight,
                        originalFileWidth: resource.originalFileWidth,
                      });
                    } else {
                      addNewValueInApiStoreMap(key, resource.url, setApiStore, setDataChange);
                      setCurrentResourceUrl(resource.url);
                    }
                    onClose();
                  }
                } else {
                  setResourceUploadState(LoadingStatus.ERROR);
                }
              }}
              onerror={(status, file) => {
                logger.error(
                  "Error while loading file ",
                  file?.filename,
                  " with error code ",
                  status.code,
                );
              }}
              credits={false}
            />
            <Typography align="center">
              {t("admin:resource.upload.file.recommendation.video")}
            </Typography>
          </Box>
          <Divider
            orientation="horizontal"
            flexItem
            sx={{
              marginTop: 1,
              marginBottom: 1,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", fontWeight: "thin" }} variant="body1">
              {t("admin:resource.upload.divider.label")}
            </Typography>
          </Divider>
          <Box
            sx={{
              paddingTop: 0.5,
              flexDirection: "row",
              display: "flex",
              justifyContent: "center",
            }}
          >
            {!openKpkKeepicker && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  alignItems: "center",
                }}
              >
                <Button
                  variant="contained"
                  disabled={!isKeepickerInRealm}
                  onClick={() => setOpenKpkKeepicker(true)}
                  sx={{ width: "fit-content" }}
                >
                  {t("admin:resource.upload.button.dam.label")}
                </Button>
                {!isKeepickerInRealm && (
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {t("admin:resource.upload.keepicker.not.available.label")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          {openKpkKeepicker && (
            <>
              <kpk-keepicker
                id="keepicker"
                ref={handleKeepickerRef}
                keycloak-client-id={KEEPICKER_VIDEO_CLIENT_ID}
                api-endpoint={process.env.NEXT_PUBLIC_API_ENDPOINT}
                data-locale="FR"
                ui-locale="FR"
              ></kpk-keepicker>
            </>
          )}
        </>
      </DialogContent>
    </Dialog>
  );
};

export default DialogVideo;
